// Permission-based Access Control System
// Similar to Overseerr's granular permission system

export enum Permission {
  // Admin Permissions
  ADMIN = 'admin', // Full administrator access. Bypasses all other permission checks.
  
  // User Management
  MANAGE_USERS = 'manage_users', // Grant permission to manage users. Cannot modify admin users.
  
  // Employee Management
  VIEW_EMPLOYEES = 'view_employees', // Grant permission to view employee data
  MANAGE_EMPLOYEES = 'manage_employees', // Grant permission to create, edit, and delete employees
  VIEW_EMPLOYEE_ANALYTICS = 'view_employee_analytics', // Grant permission to view employee performance analytics
  IMPORT_EMPLOYEES = 'import_employees', // Grant permission to import employees from CSV/Excel
  
  // Contractor Management
  VIEW_CONTRACTORS = 'view_contractors', // Grant permission to view contractor data
  MANAGE_CONTRACTORS = 'manage_contractors', // Grant permission to create, edit, and delete contractors
  
  // Timesheet Permissions
  VIEW_TIMESHEETS = 'view_timesheets', // Grant permission to view timesheets
  CREATE_TIMESHEETS = 'create_timesheets', // Grant permission to create new timesheets
  EDIT_TIMESHEETS = 'edit_timesheets', // Grant permission to edit existing timesheets
  DELETE_TIMESHEETS = 'delete_timesheets', // Grant permission to delete timesheets
  APPROVE_TIMESHEETS = 'approve_timesheets', // Grant permission to approve/reject timesheets
  BULK_TIMESHEET_ENTRY = 'bulk_timesheet_entry', // Grant permission to use bulk timesheet entry
  IMPORT_TIMESHEETS = 'import_timesheets', // Grant permission to import timesheets from Excel
  PROCESS_TIMESHEETS = 'process_timesheets', // Grant permission to use timesheet processor
  VIEW_TIMESHEET_REPORTS = 'view_timesheet_reports', // Grant permission to view timesheet reports
  REVIEW_EMPLOYEE_TIME = 'review_employee_time', // Grant permission to review employee time entries
  
  // Invoice Permissions
  VIEW_INVOICES = 'view_invoices', // Grant permission to view invoices
  CREATE_INVOICES = 'create_invoices', // Grant permission to create new invoices
  EDIT_INVOICES = 'edit_invoices', // Grant permission to edit existing invoices
  DELETE_INVOICES = 'delete_invoices', // Grant permission to delete invoices
  PRINT_INVOICES = 'print_invoices', // Grant permission to print invoices
  CONVERT_TIMESHEETS_TO_INVOICES = 'convert_timesheets_to_invoices', // Grant permission to convert timesheets to invoices
  BATCH_INVOICE_CONVERSION = 'batch_invoice_conversion', // Grant permission to batch convert timesheets to invoices
  GENERATE_EXCEL_INVOICES = 'generate_excel_invoices', // Grant permission to use Excel invoice generator
  
  // Analytics and Reporting
  VIEW_ANALYTICS = 'view_analytics', // Grant permission to view executive dashboard and analytics
  VIEW_FINANCIAL_REPORTS = 'view_financial_reports', // Grant permission to view financial reports
  VIEW_PERFORMANCE_REPORTS = 'view_performance_reports', // Grant permission to view performance reports
  
  // Field Electrician Permissions
  SUBMIT_TIMESHEETS = 'submit_timesheets', // Grant permission to submit own timesheets (field electricians)
  VIEW_OWN_TIMESHEETS = 'view_own_timesheets', // Grant permission to view own timesheet history
  
  // Contractor-specific Permissions
  VIEW_OWN_INVOICES = 'view_own_invoices', // Grant permission to view own invoices (contractors)
}

// Permission groups for easier management
export const PermissionGroups = {
  ADMIN: [Permission.ADMIN],
  
  TIMESHEET_STAFF: [
    Permission.VIEW_EMPLOYEES,
    Permission.MANAGE_EMPLOYEES,
    Permission.VIEW_EMPLOYEE_ANALYTICS,
    Permission.IMPORT_EMPLOYEES,
    Permission.VIEW_CONTRACTORS,
    Permission.MANAGE_CONTRACTORS,
    Permission.VIEW_TIMESHEETS,
    Permission.CREATE_TIMESHEETS,
    Permission.EDIT_TIMESHEETS,
    Permission.APPROVE_TIMESHEETS,
    Permission.BULK_TIMESHEET_ENTRY,
    Permission.IMPORT_TIMESHEETS,
    Permission.PROCESS_TIMESHEETS,
    Permission.VIEW_TIMESHEET_REPORTS,
    Permission.REVIEW_EMPLOYEE_TIME,
    Permission.VIEW_INVOICES,
    Permission.PRINT_INVOICES,
    Permission.VIEW_ANALYTICS,
  ],
  
  INVOICE_STAFF: [
    Permission.VIEW_EMPLOYEES,
    Permission.MANAGE_EMPLOYEES,
    Permission.VIEW_EMPLOYEE_ANALYTICS,
    Permission.IMPORT_EMPLOYEES,
    Permission.VIEW_CONTRACTORS,
    Permission.MANAGE_CONTRACTORS,
    Permission.VIEW_TIMESHEETS,
    Permission.CREATE_TIMESHEETS,
    Permission.EDIT_TIMESHEETS,
    Permission.BULK_TIMESHEET_ENTRY,
    Permission.IMPORT_TIMESHEETS,
    Permission.PROCESS_TIMESHEETS,
    Permission.VIEW_TIMESHEET_REPORTS,
    Permission.REVIEW_EMPLOYEE_TIME,
    Permission.VIEW_INVOICES,
    Permission.CREATE_INVOICES,
    Permission.EDIT_INVOICES,
    Permission.DELETE_INVOICES,
    Permission.PRINT_INVOICES,
    Permission.CONVERT_TIMESHEETS_TO_INVOICES,
    Permission.BATCH_INVOICE_CONVERSION,
    Permission.GENERATE_EXCEL_INVOICES,
    Permission.VIEW_ANALYTICS,
    Permission.VIEW_FINANCIAL_REPORTS,
    Permission.VIEW_PERFORMANCE_REPORTS,
  ],
  
  FIELD_ELECTRICIAN: [
    Permission.SUBMIT_TIMESHEETS,
    Permission.VIEW_OWN_TIMESHEETS,
  ],
  
  CONTRACTOR: [
    Permission.VIEW_OWN_INVOICES,
  ],
} as const;

// Permission metadata for UI display
export const PermissionMetadata: Record<Permission, {
  name: string;
  description: string;
  category: string;
  dangerous?: boolean; // Permissions that should be highlighted as potentially dangerous
}> = {
  [Permission.ADMIN]: {
    name: 'Admin',
    description: 'Full administrator access. Bypasses all other permission checks.',
    category: 'Administration',
    dangerous: true,
  },
  
  [Permission.MANAGE_USERS]: {
    name: 'Manage Users',
    description: 'Grant permission to manage users. Cannot modify admin users.',
    category: 'User Management',
    dangerous: true,
  },
  
  [Permission.VIEW_EMPLOYEES]: {
    name: 'View Employees',
    description: 'Grant permission to view employee data.',
    category: 'Employee Management',
  },
  
  [Permission.MANAGE_EMPLOYEES]: {
    name: 'Manage Employees',
    description: 'Grant permission to create, edit, and delete employees.',
    category: 'Employee Management',
  },
  
  [Permission.VIEW_EMPLOYEE_ANALYTICS]: {
    name: 'View Employee Analytics',
    description: 'Grant permission to view employee performance analytics.',
    category: 'Employee Management',
  },
  
  [Permission.IMPORT_EMPLOYEES]: {
    name: 'Import Employees',
    description: 'Grant permission to import employees from CSV/Excel files.',
    category: 'Employee Management',
  },
  
  [Permission.VIEW_CONTRACTORS]: {
    name: 'View Contractors',
    description: 'Grant permission to view contractor data.',
    category: 'Contractor Management',
  },
  
  [Permission.MANAGE_CONTRACTORS]: {
    name: 'Manage Contractors',
    description: 'Grant permission to create, edit, and delete contractors.',
    category: 'Contractor Management',
  },
  
  [Permission.VIEW_TIMESHEETS]: {
    name: 'View Timesheets',
    description: 'Grant permission to view all timesheets.',
    category: 'Timesheet Management',
  },
  
  [Permission.CREATE_TIMESHEETS]: {
    name: 'Create Timesheets',
    description: 'Grant permission to create new timesheets.',
    category: 'Timesheet Management',
  },
  
  [Permission.EDIT_TIMESHEETS]: {
    name: 'Edit Timesheets',
    description: 'Grant permission to edit existing timesheets.',
    category: 'Timesheet Management',
  },
  
  [Permission.DELETE_TIMESHEETS]: {
    name: 'Delete Timesheets',
    description: 'Grant permission to delete timesheets.',
    category: 'Timesheet Management',
    dangerous: true,
  },
  
  [Permission.APPROVE_TIMESHEETS]: {
    name: 'Approve Timesheets',
    description: 'Grant permission to approve or reject timesheets.',
    category: 'Timesheet Management',
  },
  
  [Permission.BULK_TIMESHEET_ENTRY]: {
    name: 'Bulk Timesheet Entry',
    description: 'Grant permission to use bulk timesheet entry features.',
    category: 'Timesheet Management',
  },
  
  [Permission.IMPORT_TIMESHEETS]: {
    name: 'Import Timesheets',
    description: 'Grant permission to import timesheets from Excel files.',
    category: 'Timesheet Management',
  },
  
  [Permission.PROCESS_TIMESHEETS]: {
    name: 'Process Timesheets',
    description: 'Grant permission to use the timesheet processor.',
    category: 'Timesheet Management',
  },
  
  [Permission.VIEW_TIMESHEET_REPORTS]: {
    name: 'View Timesheet Reports',
    description: 'Grant permission to view timesheet reports and analytics.',
    category: 'Timesheet Management',
  },
  
  [Permission.REVIEW_EMPLOYEE_TIME]: {
    name: 'Review Employee Time',
    description: 'Grant permission to review and analyze employee time entries.',
    category: 'Timesheet Management',
  },
  
  [Permission.VIEW_INVOICES]: {
    name: 'View Invoices',
    description: 'Grant permission to view all invoices.',
    category: 'Invoice Management',
  },
  
  [Permission.CREATE_INVOICES]: {
    name: 'Create Invoices',
    description: 'Grant permission to create new invoices.',
    category: 'Invoice Management',
  },
  
  [Permission.EDIT_INVOICES]: {
    name: 'Edit Invoices',
    description: 'Grant permission to edit existing invoices.',
    category: 'Invoice Management',
  },
  
  [Permission.DELETE_INVOICES]: {
    name: 'Delete Invoices',
    description: 'Grant permission to delete invoices.',
    category: 'Invoice Management',
    dangerous: true,
  },
  
  [Permission.PRINT_INVOICES]: {
    name: 'Print Invoices',
    description: 'Grant permission to print and export invoices.',
    category: 'Invoice Management',
  },
  
  [Permission.CONVERT_TIMESHEETS_TO_INVOICES]: {
    name: 'Convert Timesheets to Invoices',
    description: 'Grant permission to convert timesheets into invoices.',
    category: 'Invoice Management',
  },
  
  [Permission.BATCH_INVOICE_CONVERSION]: {
    name: 'Batch Invoice Conversion',
    description: 'Grant permission to batch convert multiple timesheets to invoices.',
    category: 'Invoice Management',
  },
  
  [Permission.GENERATE_EXCEL_INVOICES]: {
    name: 'Generate Excel Invoices',
    description: 'Grant permission to use the Excel invoice generator.',
    category: 'Invoice Management',
  },
  
  [Permission.VIEW_ANALYTICS]: {
    name: 'View Analytics',
    description: 'Grant permission to view executive dashboard and analytics.',
    category: 'Analytics & Reporting',
  },
  
  [Permission.VIEW_FINANCIAL_REPORTS]: {
    name: 'View Financial Reports',
    description: 'Grant permission to view financial reports and data.',
    category: 'Analytics & Reporting',
  },
  
  [Permission.VIEW_PERFORMANCE_REPORTS]: {
    name: 'View Performance Reports',
    description: 'Grant permission to view performance reports and metrics.',
    category: 'Analytics & Reporting',
  },
  
  [Permission.SUBMIT_TIMESHEETS]: {
    name: 'Submit Timesheets',
    description: 'Grant permission to submit own timesheets.',
    category: 'Field Operations',
  },
  
  [Permission.VIEW_OWN_TIMESHEETS]: {
    name: 'View Own Timesheets',
    description: 'Grant permission to view own timesheet history.',
    category: 'Field Operations',
  },
  
  [Permission.VIEW_OWN_INVOICES]: {
    name: 'View Own Invoices',
    description: 'Grant permission to view own invoices.',
    category: 'Contractor Access',
  },
};

// Get all permission categories for UI organization
export const getPermissionCategories = (): string[] => {
  const categories = new Set<string>();
  Object.values(PermissionMetadata).forEach(meta => categories.add(meta.category));
  return Array.from(categories).sort();
};

// Get permissions by category for UI display
export const getPermissionsByCategory = (category: string): Permission[] => {
  return Object.entries(PermissionMetadata)
    .filter(([, meta]) => meta.category === category)
    .map(([permission]) => permission as Permission);
};

// Utility type for permission arrays
export type UserPermissions = Permission[];

// Legacy role migration helper
export const migrateRoleToPermissions = (role: string): Permission[] => {
  switch (role) {
    case 'admin':
      return PermissionGroups.ADMIN;
    case 'timesheet_staff':
      return PermissionGroups.TIMESHEET_STAFF;
    case 'invoice_staff':
      return PermissionGroups.INVOICE_STAFF;
    case 'field_electrician':
      return PermissionGroups.FIELD_ELECTRICIAN;
    case 'contractor':
      return PermissionGroups.CONTRACTOR;
    default:
      return PermissionGroups.FIELD_ELECTRICIAN; // Default fallback
  }
};

// Permission checking utilities
export const hasPermission = (userPermissions: Permission[], requiredPermission: Permission): boolean => {
  // Admin permission bypasses all other checks
  if (userPermissions.includes(Permission.ADMIN)) {
    return true;
  }
  
  return userPermissions.includes(requiredPermission);
};

export const hasAnyPermission = (userPermissions: Permission[], requiredPermissions: Permission[]): boolean => {
  // Admin permission bypasses all other checks
  if (userPermissions.includes(Permission.ADMIN)) {
    return true;
  }
  
  return requiredPermissions.some(permission => userPermissions.includes(permission));
};

export const hasAllPermissions = (userPermissions: Permission[], requiredPermissions: Permission[]): boolean => {
  // Admin permission bypasses all other checks
  if (userPermissions.includes(Permission.ADMIN)) {
    return true;
  }
  
  return requiredPermissions.every(permission => userPermissions.includes(permission));
};